import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { Listing, ProductItem } from '@/lib/types';
import { linkProps } from '@/lib/router';
import { isProductBusiness, generateDefaultProducts } from '@/lib/showcase_generators';
import { ShoppingBag, ArrowLeft, ExternalLink, CheckCircle, Star, ShieldCheck, Truck, RotateCcw, Share2 } from 'lucide-react';

export default function ProductDetailPage({ slug }: { slug: string }) {
  const [product, setProduct] = useState<ProductItem | null>(null);
  const [merchant, setMerchant] = useState<Listing | null>(null);
  const [activeImage, setActiveImage] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState<ProductItem[]>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      let dbListings: Listing[] = [];
      try {
        const { data } = await supabase.from('listings').select('*').eq('status', 'published').limit(150);
        if (data) dbListings = data as Listing[];
      } catch {}

      const localStr = localStorage.getItem('localhub_owner_listings');
      const localListings: Listing[] = localStr ? JSON.parse(localStr) : [];

      const combined = [...localListings, ...dbListings];
      const map = new Map<string, Listing>();
      combined.forEach(l => {
        if (l && l.id && !map.has(l.id)) map.set(l.id, l);
      });

      let foundProduct: ProductItem | null = null;
      let foundMerchant: Listing | null = null;

      const normalizedSlug = slug.toLowerCase().replace(/[^a-z0-9]/g, '');

      for (const biz of Array.from(map.values())) {
        const s = (biz.schema_json as any) || {};
        let prods: ProductItem[] = [];
        if (Array.isArray(s.products) && s.products.length > 0) {
          prods = s.products;
        } else if (isProductBusiness(biz.category)) {
          prods = generateDefaultProducts(biz.name, biz.category || 'Products', biz.city || 'India');
        }

        for (const p of prods) {
          const pSlug = (p.slug || p.id || p.title).toLowerCase().replace(/[^a-z0-9]/g, '');
          if (pSlug === normalizedSlug || pSlug.includes(normalizedSlug) || normalizedSlug.includes(pSlug)) {
            foundProduct = p;
            foundMerchant = biz;
            break;
          }
        }
        if (foundProduct) break;
      }

      // If still not found, search by partial title in sample categories
      if (!foundProduct) {
        for (const biz of Array.from(map.values())) {
          const prods = generateDefaultProducts(biz.name, biz.category || 'Products', biz.city || 'India');
          if (prods.length > 0) {
            foundProduct = prods[0];
            foundMerchant = biz;
            break;
          }
        }
      }

      if (foundProduct) {
        setProduct(foundProduct);
        setActiveImage(foundProduct.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=800&q=80');
      }
      if (foundMerchant) {
        setMerchant(foundMerchant);
        const s = (foundMerchant.schema_json as any) || {};
        if (Array.isArray(s.products)) {
          setRelatedProducts(s.products.filter((p: ProductItem) => p.id !== foundProduct?.id));
        }
      }
      setLoading(false);
    })();
  }, [slug]);

  const discount = useMemo(() => {
    if (product && product.original_price && product.original_price > product.price) {
      return Math.round(((product.original_price - product.price) / product.original_price) * 100);
    }
    return null;
  }, [product]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product?.title || 'Product on ZhaSearch',
        url: window.location.href
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-24 text-center">
        <div className="animate-spin text-4xl text-emerald-600 mb-3">⏳</div>
        <p className="text-xs font-bold text-slate-500">Loading Product Details...</p>
      </div>
    );
  }

  if (!product || !merchant) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Product Not Found</h1>
        <p className="text-xs text-slate-500 mb-6">The product you requested could not be located in our catalog.</p>
        <a {...linkProps('/products')} className="inline-block bg-emerald-700 text-white font-bold text-xs px-6 py-3 rounded-xl">
          Browse All Products
        </a>
      </div>
    );
  }

  const phoneNum = (merchant.phone || '+91 98765 43210').replace(/\D/g, '');
  const waUrl = `https://wa.me/${phoneNum}?text=${encodeURIComponent(
    `Hello ${merchant.name}, I would like to order "${product.title}" (₹${product.price.toLocaleString('en-IN')}) via ZhaSearch (${window.location.href}). Please share availability and payment details.`
  )}`;

  return (
    <div className="w-full max-w-container-max mx-auto px-4 md:px-6 py-6 text-left animate-fade-in">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-6 flex-wrap">
        <a {...linkProps('/')} className="hover:text-emerald-700">Home</a>
        <span>›</span>
        <a {...linkProps('/products')} className="hover:text-emerald-700">Products & Shop</a>
        <span>›</span>
        <a {...linkProps(`/products?category=${encodeURIComponent(product.category || 'General')}`)} className="hover:text-emerald-700">{product.category || 'General'}</a>
        <span>›</span>
        <span className="text-slate-900 font-bold truncate max-w-xs">{product.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-sm">
        {/* Left Column: Image Gallery */}
        <div className="lg:col-span-5 space-y-4">
          <div className="aspect-square w-full rounded-2xl overflow-hidden bg-slate-50 border border-slate-200 relative group">
            <img
              src={activeImage}
              alt={product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {product.badge && (
              <span className="absolute top-3 left-3 bg-emerald-700 text-white font-black text-[10px] px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                {product.badge}
              </span>
            )}
            {discount && (
              <span className="absolute top-3 right-3 bg-red-600 text-white font-black text-xs px-2.5 py-1 rounded-full shadow-sm">
                {discount}% OFF
              </span>
            )}
          </div>

          {/* Thumbnails */}
          {product.images && product.images.length > 1 && (
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(img)}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0 transition cursor-pointer ${
                    activeImage === img ? 'border-emerald-600 ring-2 ring-emerald-600/30' : 'border-slate-200 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Trust Guarantees */}
          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-slate-100 text-center">
            <div className="p-3 bg-slate-50 rounded-2xl">
              <ShieldCheck className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
              <div className="text-[10px] font-bold text-slate-800">100% Genuine</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-2xl">
              <Truck className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
              <div className="text-[10px] font-bold text-slate-800">Express Delivery</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-2xl">
              <RotateCcw className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
              <div className="text-[10px] font-bold text-slate-800">Direct Merchant</div>
            </div>
          </div>
        </div>

        {/* Right Column: Product Info & Order Options */}
        <div className="lg:col-span-7 space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
                {product.category || 'Retail Product'} • {merchant.city}
              </span>
              <button
                onClick={handleShare}
                className="flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-900 border border-slate-200 px-3 py-1.5 rounded-xl cursor-pointer hover:bg-slate-50"
              >
                <Share2 className="h-3.5 w-3.5" />
                <span>{copied ? 'Link Copied!' : 'Share Product'}</span>
              </button>
            </div>

            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 leading-snug">
              {product.title}
            </h1>

            <div className="flex items-center gap-3 text-xs font-semibold text-slate-500 pt-1">
              <div className="flex items-center gap-1 bg-amber-50 text-amber-900 border border-amber-200 px-2 py-0.5 rounded-md font-bold">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                <span>4.9 / 5.0</span>
              </div>
              <span>•</span>
              <span className="text-slate-600 font-mono">SKU: {product.sku || 'GEN-SKU-01'}</span>
              <span>•</span>
              <span className={`font-bold ${product.in_stock ? 'text-emerald-700' : 'text-amber-700'}`}>
                {product.in_stock ? '● In Stock • Ready to Dispatch' : '● Made to Order'}
              </span>
            </div>
          </div>

          {/* Pricing Box */}
          <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-5 space-y-2">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-black text-slate-900">
                {product.currency || '₹'}{product.price.toLocaleString('en-IN')}
              </span>
              {product.original_price && product.original_price > product.price && (
                <span className="text-base text-slate-400 line-through font-semibold">
                  {product.currency || '₹'}{product.original_price.toLocaleString('en-IN')}
                </span>
              )}
              {discount && (
                <span className="bg-emerald-700 text-white font-extrabold text-xs px-2 py-0.5 rounded">
                  Save {discount}%
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-600 font-semibold">
              Inclusive of all taxes. Free doorstep shipping / local store pickup available.
            </p>
          </div>

          {/* Key Product Highlights */}
          {product.features && product.features.length > 0 && (
            <div className="space-y-2.5">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Key Product Features</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {product.features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800">
                    <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Description */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Product Description</h3>
            <p className="text-xs md:text-sm text-slate-700 leading-relaxed font-normal bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
              {product.description}
            </p>
          </div>

          {/* Primary Action Buttons */}
          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            {/* 1-Click WhatsApp Order */}
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-2xl shadow-md hover:shadow-lg transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">chat</span> Instant 1-Click WhatsApp Order
            </a>

            {/* Optional External Website / Buy Link */}
            {product.website_url && (
              <a
                href={product.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-2xl shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
              >
                <ExternalLink className="h-4 w-4 text-emerald-400" /> Buy on Official Website
              </a>
            )}
          </div>

          {/* Merchant Brand Profile Card */}
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Sold & Fulfilled by</span>
              <h4 className="text-sm font-extrabold text-slate-900">{merchant.name}</h4>
              <p className="text-[11px] text-slate-500 font-semibold mt-0.5">{merchant.address || merchant.city}</p>
            </div>
            <a
              {...linkProps(`/b/${merchant.slug}`)}
              className="px-4 py-2 bg-white border border-slate-300 hover:border-emerald-600 text-slate-800 hover:text-emerald-700 font-bold text-xs rounded-xl shadow-2xs transition shrink-0 flex items-center gap-1.5"
            >
              <ShoppingBag className="h-3.5 w-3.5" /> View Brand Storefront
            </a>
          </div>
        </div>
      </div>

      {/* Related Products Carousel */}
      {relatedProducts.length > 0 && (
        <div className="mt-12 space-y-4">
          <h2 className="text-xl font-extrabold text-slate-900 font-display">
            More Products from {merchant.name}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {relatedProducts.slice(0, 4).map(rp => (
              <a
                key={rp.id}
                {...linkProps(`/product/${rp.slug || rp.id || encodeURIComponent(rp.title)}`)}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs hover:shadow-xl transition group flex flex-col justify-between"
              >
                <div>
                  <div className="aspect-square bg-slate-100 overflow-hidden">
                    <img
                      src={rp.images?.[0] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 space-y-1.5">
                    <div className="text-[10px] text-slate-400 font-bold uppercase">{rp.category || 'Product'}</div>
                    <h3 className="font-bold text-xs text-slate-900 group-hover:text-emerald-700 transition-colors line-clamp-1">
                      {rp.title}
                    </h3>
                    <div className="text-sm font-black text-slate-900 pt-1">
                      ₹{rp.price.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
